import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

/**
 * StackedBarChartWidget - Renders a grouped and stacked bar chart
 * 
 * Data Transformation:
 * - Pivots flat data (xLabel, seriesName, yValue, metadata) into series format
 * - Parses `metadata` JSON to find "stack" property for grouping series
 * 
 * @param {Array} data - Flat array of data points
 * @param {Object} config - Widget configuration
 */
export default function StackedBarChartWidget({ data, config, height = 300 }) {
  
  const processedData = useMemo(() => {
    // 1. Get unique X-Axis Labels (categories) and Series Names
    // Use Set to ensure uniqueness and preserve insertion order (if sorted from backend)
    const xLabelsSet = new Set(data.map(item => item.xLabel));
    const xLabels = Array.from(xLabelsSet);

    // 2. Group data by seriesName
    const seriesMap = {};
    const seriesOrder = []; // To preserve backend sort order if implicit

    data.forEach(item => {
      if (!item.seriesName) return;
      
      if (!seriesMap[item.seriesName]) {
        seriesMap[item.seriesName] = [];
        seriesOrder.push(item.seriesName);
      }
      seriesMap[item.seriesName].push(item);
    });

    // Default MUI-like palette for fallback
    const defaultPalette = [
        '#02B2AF', '#2E96FF', '#B800D8', '#60009B', '#2731C8', 
        '#03008D', '#00A3A1', '#007D7B', '#005856', '#003635'
    ];

    // 3. Build MUI Series Objects
    const series = seriesOrder.map((seriesName, index) => {
      const items = seriesMap[seriesName];
      
      // Parse metadata for stack group, e.g. metdata = '{"stack": "A", "color": "#ff0000"}'
      let stackGroup = undefined;
      // Default to palette color based on index
      let seriesColor = defaultPalette[index % defaultPalette.length];
      
      try {
        if (items[0]?.metadata) {
            const meta = JSON.parse(items[0].metadata);
            if (meta.stack) {
                stackGroup = meta.stack;
            }
            if (meta.color) {
                seriesColor = meta.color;
            }
        }
      } catch (e) {
        console.warn("Failed to parse metadata for widget series", seriesName, e);
      }

      // Map values to match the global xLabels order
      // If a series is missing a value for a label, default to null (no bar) or 0
      const seriesData = xLabels.map(label => {
        const point = items.find(i => i.xLabel === label);
        return point ? parseFloat(point.yValue) : null;
      });

      return {
        data: seriesData,
        label: seriesName,
        stack: stackGroup,
        color: seriesColor,
      };
    });

    return { xLabels, series };
  }, [data]);

  if (!processedData.xLabels.length) {
      return null;
  }

  const isHorizontal = config.widgetType && config.widgetType.includes('HORI');

  if (isHorizontal) {
    // 1. Calculate Dynamic Height
    const itemHeight = 40; // Tighter rows
    const axisHeight = 30;
    const chartAreaHeight = height - axisHeight;
    const minHeight = chartAreaHeight;
    const dynamicHeight = Math.max(minHeight, processedData.xLabels.length * itemHeight);
    const needsScroll = dynamicHeight > chartAreaHeight;
    const scrollbarWidth = 10;

    // 2. Calculate Domain Max (Sum of stacked values per xLabel)
    // We need to sum up all positive values for each xLabel to find the max stack height
    const stackSums = processedData.xLabels.map((_, index) => {
        let sum = 0;
        processedData.series.forEach(s => {
            const val = s.data[index] || 0;
            if (val > 0) sum += val;
        });
        return sum;
    });
    const maxStackVal = Math.max(...stackSums, 0);
    const domainMax = maxStackVal === 0 ? 10 : maxStackVal * 1.1; // Add 10% buffer

    const xAxisConfig = [{
        min: 0,
        max: domainMax,
    }];

    return (
        <Box>
             {/* Legend (Top) */}
             <Box display="flex" justifyContent="center" mb={1} flexWrap="wrap">
                {processedData.series.map((s, i) => (
                    <Box key={i} display="flex" alignItems="center" mx={1}>
                        <Box width={12} height={12} bgcolor={s.color} mr={0.5} /> 
                        <span style={{fontSize: '0.75rem'}}>{s.label}</span>
                    </Box>
                ))}
             </Box>

            {/* Scrollable Bar Area */}
            <Box sx={{ height: chartAreaHeight, overflowY: 'auto', pr: 1, overflowX: 'hidden' }}>
                <BarChart
                    layout="horizontal"
                    yAxis={[{ 
                        scaleType: 'band', 
                        data: processedData.xLabels,
                        categoryGapRatio: 0.3, 
                        barGapRatio: 0.1
                    }]}
                    xAxis={xAxisConfig}
                    bottomAxis={null} // Hide bottom axis in scrollable area
                    series={processedData.series}
                    height={dynamicHeight}
                    margin={{ top: 10, right: 30, bottom: 0, left: 180 }}
                    slotProps={{
                        legend: { hidden: true } // Hide legend in scroll area (optional, or put separate)
                    }}
                />
            </Box>
            
            {/* Fixed Axis Area */}
            <Box sx={{ height: axisHeight, pr: 1 + (needsScroll ? (scrollbarWidth/8) : 0) }}>
                <BarChart
                    layout="horizontal"
                    xAxis={xAxisConfig}
                    yAxis={[{ scaleType: 'band', data: [''] }]} // Dummy Y axis
                    leftAxis={null}
                    series={[{ data: [0], color: 'transparent' }]} // Dummy series
                    height={axisHeight + 30}
                    margin={{ top: 0, right: 30, bottom: 30, left: 180 }}
                    slotProps={{ legend: { hidden: true } }}
                />
            </Box>
        </Box>
    );
  }

  // Vertical (Standard) Layout
  return (
    <Box>
      <BarChart
        layout="vertical"
        xAxis={[{ 
          scaleType: 'band', 
          data: processedData.xLabels,
          categoryGapRatio: 0.3, 
          barGapRatio: 0.1
        }]}
        series={processedData.series}
        height={height}
        margin={{ top: 10, right: 10, bottom: 50, left: 60 }}
        slotProps={{
            legend: {
                direction: 'row',
                position: { vertical: 'top', horizontal: 'middle' },
                padding: { bottom: 20 },
            }
        }}
      />
    </Box>
  );
}
